import { Abschnitt } from '@/domain/mitarbeiter/features/rotationsplan/entities/Abschnitt';
import { Abwesenheit } from '@/domain/mitarbeiter/features/rotationsplan/entities/Abwesenheit';
import { Beruf } from '@/domain/mitarbeiter/features/rotationsplan/entities/Beruf';
import { Mitarbeiter } from '@/domain/mitarbeiter/features/rotationsplan/entities/Mitarbeiter';
import {
  AbwesenheitsStation,
  ArbeitsbereichStation,
} from '@/domain/mitarbeiter/features/rotationsplan/entities/Station';
import dayjs from '@/domain/mitarbeiter/shared/utils/dayjs';
import { t } from 'i18next';

export class Rotationsplan {
  constructor({
    id,
    mitarbeiter,
    beruf,
    abschnitte,
    standortId,
    abwesenheiten,
    land,
  }: {
    id: string;
    standortId: string;
    mitarbeiter: Mitarbeiter;
    beruf: Beruf;
    abschnitte: Abschnitt[];
    abwesenheiten: Abwesenheit[];
    land: string;
  }) {
    this.id = id;
    this.mitarbeiter = mitarbeiter;
    this.beruf = beruf;
    this.abschnitte = abschnitte;
    this.standortId = standortId;
    this.abwesenheiten = abwesenheiten;
    this.land = land;
  }

  id: string;
  standortId: string;
  mitarbeiter: Mitarbeiter;
  beruf: Beruf;
  abschnitte: Abschnitt[];
  abwesenheiten: Abwesenheit[];
  land: string;

  get currentLehrjahr(): string {
    const today = dayjs();
    const currentAbschnitt = this.abschnitte.find((abschnitt) =>
      abschnitt.stationen.some(
        (station) =>
          dayjs(station.von).isBefore(today, 'day') &&
          dayjs(station.bis).isAfter(today, 'day'),
      ),
    );
    if (currentAbschnitt) {
      return currentAbschnitt.position + '. ' + currentAbschnitt.name;
    }

    const abgeschlosseneStationen = this.abgeschlosseneStationen;
    const letzteAbgeschlosseneStation =
      abgeschlosseneStationen.length >= 1
        ? abgeschlosseneStationen[abgeschlosseneStationen.length - 1]
        : undefined;

    if (letzteAbgeschlosseneStation) {
      const abschnitt = this.abschnitte.find(
        (abschnitt) => letzteAbgeschlosseneStation.abschnittId === abschnitt.id,
      );
      return abschnitt.name;
    }

    return t('domain.mitarbeiter.rotationsplan.rotationsplaene.geplant');
  }

  get currentStation():
    ArbeitsbereichStation | AbwesenheitsStation | undefined {
    const today = dayjs();
    for (const abschnitt of this.abschnitte) {
      const station = abschnitt.stationen.find(
        (station) =>
          dayjs(station.von).isSameOrBefore(today, 'day') &&
          dayjs(station.bis).isSameOrAfter(today, 'day'),
      );
      if (station) {
        return station;
      }
    }
    return undefined;
  }

  get abgeschlosseneStationen(): (
    ArbeitsbereichStation | AbwesenheitsStation
  )[] {
    const today = dayjs();
    const abgeschlosseneStationen: (
      ArbeitsbereichStation | AbwesenheitsStation
    )[] = [];

    for (const abschnitt of this.abschnitte) {
      for (const station of abschnitt.stationen) {
        if (dayjs(station.bis).isBefore(today, 'day')) {
          abgeschlosseneStationen.push(station);
        }
      }
    }

    return abgeschlosseneStationen;
  }

  get nextStation(): ArbeitsbereichStation | AbwesenheitsStation | undefined {
    const today = dayjs();
    let nextStation: ArbeitsbereichStation | AbwesenheitsStation | undefined;

    const stationen = this.abschnitte
      .flatMap((abschnitt) => abschnitt.stationen)
      .sort((a, b) => dayjs(a.von).valueOf() - dayjs(b.von).valueOf());

    for (const station of stationen) {
      if (dayjs(station.von).isAfter(today, 'day')) {
        nextStation = station;
        break;
      }
    }

    return nextStation;
  }

  get amountOfStationen(): number {
    return this.abschnitte.reduce((acc, curr) => {
      return (acc += curr.stationen.length);
    }, 0);
  }

  get allStationen(): Array<ArbeitsbereichStation> {
    return this.abschnitte
      .flatMap((abschnitt) => abschnitt.stationen)
      .filter((station) => station instanceof ArbeitsbereichStation)
      .sort((a, b) => dayjs(a.von).valueOf() - dayjs(b.von).valueOf());
  }

  abwesenheitenByStationId(stationId: string): Abwesenheit[] {
    const station = this.allStationen.find((s) => s.id === stationId);
    if (!station) {
      return [];
    }

    const filteredAbwesenheiten = this.abwesenheiten.filter((abwesenheit) => {
      return (
        dayjs(abwesenheit.von).isSameOrBefore(dayjs(station.bis), 'day') &&
        dayjs(abwesenheit.bis).isSameOrAfter(dayjs(station.von), 'day')
      );
    });
    return filteredAbwesenheiten;
  }
}
